import BaseAccordion from 'components/Accordions/BaseAccordion';
import BaseTable from 'components/Tables/BaseTable';
import type { BaseTableColumnDef } from 'components/Tables/baseTablePreferences';
import BaseTooltip from 'components/Tooltips/BaseTooltip';
import { usePerms } from 'contexts/PermsContext';
import { useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import styles from '../css/ServiceManager.module.css';
import { pendingChangeKey, useWorkplaces } from '../hooks/Deployments.hooks';
import ForceUnsafeRootModal from '../modals/ForceUnsafeRootModal';
import RemoveWorkplaceModal from '../modals/RemoveWorkplaceModal';
import ServiceDisableModal from '../modals/ServiceDisableModal';
import StatusDot, { VARIANT_TEXT_CLASS } from './StatusDot';
import WorkplaceFormModal from '../modals/WorkplaceFormModal';
import type {
  AddWorkplaceRequest,
  GlobalService,
  ToggleOutcome,
  ToggleTarget,
  UpdateWorkplaceRequest,
  Workplace,
  WorkplaceService,
} from '../types/Deployments.types';
import {
  deploymentModeCaveat,
  isEnabledButNotRunning,
  liveStateView,
  toggleBlockedReason,
  toggleRowKey,
} from '../utils/ServiceManager.utils';

const SOURCE_GLOBAL_TOOLTIP =
  'Inherits global. No per-deployment override is set, so this service runs, or does not, ' +
  'according to the global flag.';

const SOURCE_OVERRIDDEN_TOOLTIP =
  'Overridden here. A per-deployment override is set, forcing the service on or off for this ' +
  'deployment regardless of the global flag.';

const CONFIGURED_TOOLTIP =
  'Configured is what this deployment is set up to run, and it persists across restarts. ' +
  'Turning it on writes the flag and the supervisor spawns the process on its next tick; ' +
  'turning it off stops the process straight away. No supervisor restart is needed either way.';

const LIVE_TOOLTIP =
  'Live is what PM2 actually has right now. It is a separate question from Configured: ' +
  'enabling a service is not the same as it successfully starting.';

/**
 * The Deployments tab.
 *
 * Lists every deployment this supervisor owns and, for each, the services it is
 * configured to run. This is the only surface where a service that is turned
 * OFF exists at all: the Services tab is built from the supervisor's registry,
 * and a service whose flag is off has no registry row and no PM2 process.
 *
 * Every service row shows two independent facts, deliberately in separate
 * columns:
 *   Configured  what this deployment is set up to run (`effective`)
 *   Live (PM2)  what is actually up right now (`state` / `running`)
 * A green switch drawn over a crash-loop is the failure that separation
 * prevents.
 */
export default function Deployments() {
  const {
    payload,
    isLoading,
    isRefreshing,
    reqTime,
    error,
    refresh,
    busyKey,
    outcomes,
    pendingToggle,
    requestToggle,
    confirmToggle,
    cancelToggle,
    pendingWorkplaceChanges,
    addWorkplace,
    updateWorkplace,
    removeWorkplace,
    dismissPendingChange,
    restartSupervisor,
    isRestarting,
  } = useWorkplaces();

  // 503 from POST /admin/supervisor/restart: the supervisor is not under PM2
  // (dev mode). We render the manualRestartHint as a code block under the
  // banner; cleared on the next successful 202 or on Dismiss.
  const [manualRestartHint, setManualRestartHint] = useState<string | null>(null);

  const perms = usePerms();
  const canToggle = perms?.hasDeploymentManagerAccess?.() ?? false;
  // Same gate as the toggle: every structural operation needs deployment
  // manager access.
  const canMutateStructural = canToggle;

  // The form modal handles both add and edit; a null target means add. We store
  // the captured workplace's `index` and resolve the latest object from the
  // current payload, so a refetch (after a 409, say) refreshes the lockVersion
  // the modal sees.
  const [formModal, setFormModal] = useState<{
    show: boolean;
    targetIndex: number | null;
  }>({ show: false, targetIndex: null });

  const [removeTargetIndex, setRemoveTargetIndex] = useState<number | null>(null);

  // Two-step forceUnsafeRoot flow: the first submit hits a 400 from the
  // supervisor's `.fusion-backend.json` check; we surface a confirmation and
  // resubmit with the flag. The resolver ref bridges between handleFormSubmit,
  // which awaits a promise, and the modal's button handlers, which resolve it.
  const [unsafePrompt, setUnsafePrompt] = useState<{
    rootDir: string;
    serverMessage: string;
  } | null>(null);
  const unsafeResolverRef = useRef<((proceed: boolean) => void) | null>(null);

  const currentEditTarget: Workplace | null =
    formModal.targetIndex != null
      ? (payload?.workplaces.find((w) => w.index === formModal.targetIndex) ?? null)
      : null;
  const currentRemoveTarget: Workplace | null =
    removeTargetIndex != null
      ? (payload?.workplaces.find((w) => w.index === removeTargetIndex) ?? null)
      : null;

  // Derive uniqueness lists, known service flags and a root-dir template from
  // the listing, so the Add modal can short-circuit collisions and suggest
  // defaults matching the host's existing convention.
  const { existingPrefixes, existingPorts, existingIds, knownEnvFlags, suggestedRootBase } =
    useMemo(() => {
      const prefixes = new Set<string>();
      const ports = new Set<number>();
      const ids = new Set<number>();
      const flags = new Set<string>();
      payload?.workplaces.forEach((wp) => {
        prefixes.add(wp.prefix);
        ports.add(wp.port);
        ids.add(wp.id);
        wp.services.forEach((svc) => {
          if (svc.envFlag) flags.add(svc.envFlag);
        });
      });
      payload?.globalServices.forEach((svc) => {
        if (svc.envFlag) flags.add(svc.envFlag);
      });

      // Derive a base path from an existing workplace's rootDir so new
      // workplaces on the same supervisor get a host-correct suggestion. The
      // canonical convention is "<host-parent>/BackendServer_Fusion_<Prefix>",
      // but the original Demo and Dev workplaces use a bare
      // "BackendServer_Fusion" with no suffix, so we anchor on the literal
      // marker rather than the last underscore.
      const marker = 'BackendServer_Fusion';
      const sample = payload?.workplaces.find((wp) => wp.rootDir.includes(marker))?.rootDir;
      let base = '';
      if (sample) {
        const idx = sample.indexOf(marker);
        base = `${sample.slice(0, idx + marker.length)}_`;
      }

      return {
        existingPrefixes: Array.from(prefixes),
        existingPorts: Array.from(ports),
        existingIds: Array.from(ids),
        knownEnvFlags: Array.from(flags).sort(),
        suggestedRootBase: base,
      };
    }, [payload]);

  const errStatus = (err: unknown): number | undefined =>
    (err as { response?: { status?: number } })?.response?.status;

  const errMessage = (err: unknown): string =>
    (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '';

  /**
   * The supervisor's 400 from the `.fusion-backend.json` check ends with a
   * "resend with forceUnsafeRoot: true" hint. Match the literal flag name so we
   * do not pick up unrelated 400s such as a prefix collision.
   */
  const isRootDirValidationError = (err: unknown): boolean =>
    errStatus(err) === 400 && errMessage(err).toLowerCase().includes('forceunsaferoot');

  const promptForceUnsafeRoot = (rootDir: string, serverMessage: string): Promise<boolean> =>
    new Promise<boolean>((resolve) => {
      unsafeResolverRef.current = resolve;
      setUnsafePrompt({ rootDir, serverMessage });
    });

  const onUnsafePromptConfirm = () => {
    const resolver = unsafeResolverRef.current;
    unsafeResolverRef.current = null;
    setUnsafePrompt(null);
    resolver?.(true);
  };

  const onUnsafePromptCancel = () => {
    const resolver = unsafeResolverRef.current;
    unsafeResolverRef.current = null;
    setUnsafePrompt(null);
    resolver?.(false);
  };

  const handleFormSubmit = async (
    submission:
      | { mode: 'add'; body: AddWorkplaceRequest }
      | { mode: 'edit'; wpIndex: number; body: UpdateWorkplaceRequest },
  ) => {
    // Inner doSubmit so we can call it twice for the two-step forceUnsafeRoot
    // flow: first without the flag, then with it.
    const doSubmit = async (
      sub:
        | { mode: 'add'; body: AddWorkplaceRequest }
        | { mode: 'edit'; wpIndex: number; body: UpdateWorkplaceRequest },
    ) => {
      if (sub.mode === 'add') {
        const result = await addWorkplace(sub.body);
        const verb = result?.restartRequired
          ? 'queued. Restart the supervisor to activate it'
          : 'added';
        toast.success(`Deployment "${sub.body.name}" ${verb}.`);
        return;
      }
      if (!currentEditTarget) return;
      const body: UpdateWorkplaceRequest = {
        lockVersion: currentEditTarget.lockVersion,
        ...sub.body,
      };
      const result = await updateWorkplace(sub.wpIndex, body, {
        id: currentEditTarget.id,
        name: currentEditTarget.name,
        prefix: currentEditTarget.prefix,
        lockVersion: currentEditTarget.lockVersion,
      });
      const verb = result?.restartRequired
        ? 'updated. Restart the supervisor to apply the changes'
        : 'updated';
      toast.success(`Deployment "${sub.body.name ?? currentEditTarget.name}" ${verb}.`);
    };

    try {
      await doSubmit(submission);
    } catch (err) {
      if (errStatus(err) === 409) {
        // Refetch so the listing, and therefore currentEditTarget.lockVersion,
        // syncs to the latest values. The modal stays open with the operator's
        // edits so they can re-confirm and resubmit.
        refresh();
        toast.warning(
          'This deployment was changed by someone else. Review the latest values and resubmit.',
        );
        throw err;
      }
      if (isRootDirValidationError(err)) {
        const proceed = await promptForceUnsafeRoot(submission.body.rootDir ?? '', errMessage(err));
        if (!proceed) throw err;
        const retry =
          submission.mode === 'add'
            ? { mode: 'add' as const, body: { ...submission.body, forceUnsafeRoot: true } }
            : {
                mode: 'edit' as const,
                wpIndex: submission.wpIndex,
                body: { ...submission.body, forceUnsafeRoot: true },
              };
        await doSubmit(retry);
        return;
      }
      throw err;
    }
  };

  /**
   * "Restart now" on the restart-required banner. On 202 the hook clears the
   * banners and starts the back-off poll. On 503 the supervisor is not under
   * PM2, so we show the manual command for the operator to copy.
   */
  const handleRestartNow = async () => {
    setManualRestartHint(null);
    try {
      const data = await restartSupervisor('Operator used Restart now on the banner');
      if (data.restartScheduled) {
        toast.success('Supervisor restarting. The listing will refresh shortly.');
      } else {
        setManualRestartHint(
          data.manualRestartHint ||
            'The supervisor is not running under PM2. Restart it manually to activate pending changes.',
        );
      }
    } catch (err) {
      toast.error(`Restart failed. ${errMessage(err) || 'The request did not go through.'}`);
    }
  };

  const copyManualHint = async () => {
    if (!manualRestartHint) return;
    try {
      await navigator.clipboard.writeText(manualRestartHint);
      toast.success('Copied to clipboard.');
    } catch {
      toast.error('Could not copy. Select the text and copy it manually.');
    }
  };

  const handleRemoveConfirm = async (wpIndex: number, reason: string) => {
    if (!currentRemoveTarget) return;
    try {
      const result = await removeWorkplace(wpIndex, reason, {
        id: currentRemoveTarget.id,
        name: currentRemoveTarget.name,
        prefix: currentRemoveTarget.prefix,
        lockVersion: currentRemoveTarget.lockVersion,
      });
      const verb = result?.restartRequired
        ? 'queued for removal. Restart the supervisor to retire it'
        : 'removed';
      toast.success(`Deployment "${currentRemoveTarget.name}" ${verb}.`);
    } catch (err) {
      if (errStatus(err) === 409) {
        refresh();
        toast.warning(
          'This deployment was changed by someone else. Review the latest values and resubmit.',
        );
      }
      throw err;
    }
  };

  const globalServiceColumns = useMemo(
    () =>
      buildServiceColumns<GlobalService>({
        canToggle,
        busyKey,
        outcomes,
        requestToggle,
        targetFor: (row) => ({ scope: 'global', name: row.name, envFlag: row.envFlag }),
        showSource: false,
      }),
    [canToggle, busyKey, outcomes, requestToggle],
  );

  return (
    <div className="bg-base p-3 d-flex flex-column rounded-3">
      <div className="d-flex flex-column flex-md-row align-items-md-center gap-3 mb-3">
        <div className="p-0">
          <div className="text-theme fw-semibold fs-5">Deployments</div>
          <div className="text-theme small mt-1">
            {payload ? (
              <>
                Supervisor: <span className="fw-semibold">{payload.supervisorId}</span>
                {payload.supervisorVersion ? (
                  <>
                    {' '}
                    <span className="text-theme-muted">v{payload.supervisorVersion}</span>
                  </>
                ) : null}
                {payload.globalServicePrefix ? (
                  <>
                    {' · '}Globals prefix:{' '}
                    <span className="fw-semibold">{payload.globalServicePrefix}</span>
                  </>
                ) : null}
              </>
            ) : (
              <>Loading supervisor information...</>
            )}
            {reqTime && (
              <>
                {' · '}Last requested:{' '}
                {reqTime.toLocaleString(undefined, { timeZoneName: 'short' })}
              </>
            )}
          </div>
        </div>
        <div className="p-0 d-flex justify-content-end align-items-center gap-2 flex-grow-1">
          <Button disabled={isLoading || isRefreshing} onClick={refresh}>
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          {canMutateStructural && (
            <BaseTooltip
              id="deployments-add-workplace"
              text="Register a new deployment with its own prefix, port, root directory and per-service flags."
            >
              <Button
                variant="primary"
                onClick={() => setFormModal({ show: true, targetIndex: null })}
              >
                + Add deployment
              </Button>
            </BaseTooltip>
          )}
        </div>
      </div>

      {pendingWorkplaceChanges.length > 0 && (
        <div className="mb-3 d-flex flex-column gap-2">
          {/* One Restart-now control for the whole pending set: a supervisor
              restart applies every queued change at once. */}
          {canMutateStructural && (
            <div className="d-flex justify-content-end">
              <Button
                variant="warning"
                size="sm"
                onClick={handleRestartNow}
                disabled={isRestarting}
              >
                {isRestarting ? 'Restarting...' : 'Restart supervisor now'}
              </Button>
            </div>
          )}
          {manualRestartHint && (
            <Alert variant="warning" className="mb-0">
              <div className="fw-semibold mb-1">
                The supervisor is not under PM2. Restart it manually:
              </div>
              <div className="d-flex align-items-start gap-2">
                <code
                  className={`flex-grow-1 p-2 font-monospace small ${styles.manualRestartHint}`}
                >
                  {manualRestartHint}
                </code>
                <Button variant="outline-dark" size="sm" onClick={copyManualHint}>
                  Copy
                </Button>
              </div>
            </Alert>
          )}
          {pendingWorkplaceChanges.map((p) => {
            const key = pendingChangeKey(p);
            const headline =
              p.action === 'add'
                ? `Supervisor restart required to activate deployment "${p.name}".`
                : p.action === 'remove'
                  ? `Supervisor restart required to retire deployment "${p.name}".`
                  : `Supervisor restart required to apply changes to deployment "${p.name}".`;
            return (
              <div key={key} className="d-flex flex-column gap-1">
                <Alert
                  variant="warning"
                  className="d-flex justify-content-between align-items-start mb-0"
                >
                  <div>
                    <div className="fw-semibold">{headline}</div>
                    <div className="small mt-1">{p.restartHint}</div>
                  </div>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => dismissPendingChange(key)}
                  >
                    Dismiss
                  </Button>
                </Alert>
                {/* Supervisor advisories, most commonly the forceUnsafeRoot
                    coercion notice. Indented under the parent banner. */}
                {p.warnings?.map((w, i) => (
                  <Alert
                    key={`${key}-warning-${i}`}
                    variant="warning"
                    className="mb-0 ms-3 small py-2"
                  >
                    <span className="fw-semibold me-2">Advisory:</span>
                    {w}
                  </Alert>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Content flows in normal page scroll here, which avoids nested scroll
          traps when large deployment accordions or tables expand. */}
      <div className="d-flex flex-column">
        {isLoading && (
          <div className="text-theme-muted py-4 text-center">Loading deployments...</div>
        )}

        {!isLoading && error && (
          <div className="text-danger py-4 text-center">Could not load deployments: {error}</div>
        )}

        {!isLoading && !error && payload && (
          <>
            <div className="text-theme fw-semibold mb-2">Deployments</div>
            {payload.workplaces.length === 0 ? (
              <div className="text-theme-muted py-3">No deployments configured.</div>
            ) : (
              <div className="d-flex flex-column gap-3 mb-4">
                {payload.workplaces.map((wp, idx) => (
                  <div key={`${wp.index}-${wp.prefix}`} className={styles.workplaceCard}>
                    <BaseAccordion
                      eventKey={idx}
                      bodyBackground="var(--color-base)"
                      header={
                        <WorkplaceHeader
                          workplace={wp}
                          canMutate={canMutateStructural}
                          onEdit={() => setFormModal({ show: true, targetIndex: wp.index })}
                          onRemove={() => setRemoveTargetIndex(wp.index)}
                        />
                      }
                    >
                      <WorkplaceBody
                        workplace={wp}
                        canToggle={canToggle}
                        busyKey={busyKey}
                        outcomes={outcomes}
                        requestToggle={requestToggle}
                      />
                    </BaseAccordion>
                  </div>
                ))}
              </div>
            )}

            {payload.globalServices.length > 0 && (
              <>
                <div className="text-theme fw-semibold mb-1 mt-2">Global services</div>
                <div className="text-theme-muted small mb-2">
                  One shared instance per supervisor rather than one per deployment, so there is a
                  single flag and no per-deployment override.
                </div>
                <BaseTable
                  columns={globalServiceColumns}
                  rows={payload.globalServices}
                  expandable={false}
                  customStyles={{
                    cell: { borderBottom: '1px solid var(--color-divider)' },
                  }}
                  muiTableContainerProps={{
                    sx: { overflowY: 'visible', overflowX: 'auto' },
                  }}
                  customEmptyMessage="No global services to display"
                />
              </>
            )}
          </>
        )}
      </div>

      <WorkplaceFormModal
        show={formModal.show}
        mode={currentEditTarget ? 'edit' : 'add'}
        initialWorkplace={currentEditTarget ?? undefined}
        onClose={() => setFormModal({ show: false, targetIndex: null })}
        onSubmit={handleFormSubmit}
        existingPrefixes={existingPrefixes}
        existingPorts={existingPorts}
        existingIds={existingIds}
        knownEnvFlags={knownEnvFlags}
        suggestedRootBase={suggestedRootBase}
      />

      <RemoveWorkplaceModal
        show={!!currentRemoveTarget}
        workplace={currentRemoveTarget}
        onClose={() => setRemoveTargetIndex(null)}
        onConfirm={handleRemoveConfirm}
      />

      <ForceUnsafeRootModal
        show={!!unsafePrompt}
        rootDir={unsafePrompt?.rootDir ?? ''}
        serverMessage={unsafePrompt?.serverMessage ?? ''}
        onConfirm={onUnsafePromptConfirm}
        onCancel={onUnsafePromptCancel}
      />

      <ServiceDisableModal
        pending={pendingToggle}
        onConfirm={confirmToggle}
        onCancel={cancelToggle}
      />
    </div>
  );
}

interface WorkplaceHeaderProps {
  workplace: Workplace;
  canMutate: boolean;
  onEdit: () => void;
  onRemove: () => void;
}

function WorkplaceHeader({ workplace, canMutate, onEdit, onRemove }: WorkplaceHeaderProps) {
  // Stop the accordion collapsing when the operator clicks Edit or Remove.
  const stopAndRun = (fn: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    fn();
  };

  const configuredOn = workplace.services.filter((s) => s.effective).length;

  return (
    <div className="d-flex flex-column flex-md-row align-items-md-center gap-md-3 w-100 me-3">
      <div className="text-theme fw-semibold">
        {workplace.name}{' '}
        <span className="text-theme-muted small">(workplace_id {workplace.id})</span>
      </div>
      <div className="text-theme-muted small d-flex flex-wrap gap-2">
        <span>
          Prefix: <span className="text-theme">{workplace.prefix}</span>
        </span>
        <span>&middot;</span>
        <span>
          Port: <span className="text-theme">{workplace.port}</span>
        </span>
        <span>&middot;</span>
        <span>
          Mode: <span className="text-theme">{workplace.mode}</span>
        </span>
        <span>&middot;</span>
        <span>
          Services on:{' '}
          <span className="text-theme">
            {configuredOn} of {workplace.services.length}
          </span>
        </span>
      </div>
      {canMutate && (
        <div className="d-flex gap-2 ms-md-auto">
          <Button size="sm" variant="outline-light" onClick={stopAndRun(onEdit)}>
            Edit
          </Button>
          <Button size="sm" variant="outline-danger" onClick={stopAndRun(onRemove)}>
            Remove
          </Button>
        </div>
      )}
    </div>
  );
}

interface ServiceColumnOptions<T> {
  canToggle: boolean;
  busyKey: string | null;
  outcomes: Record<string, ToggleOutcome>;
  requestToggle: (target: ToggleTarget, enabled: boolean) => void;
  targetFor: (row: T) => ToggleTarget;
  /** Workplace rows show whether the value is inherited or overridden. */
  showSource: boolean;
}

/**
 * The service table columns, shared by the per-deployment tables and the global
 * table so the two can never drift into describing the same facts differently.
 */
function buildServiceColumns<T extends WorkplaceService | GlobalService>({
  canToggle,
  busyKey,
  outcomes,
  requestToggle,
  targetFor,
  showSource,
}: ServiceColumnOptions<T>): BaseTableColumnDef<T>[] {
  const columns: BaseTableColumnDef<T>[] = [
    {
      header: 'Configured',
      accessorKey: 'effective',
      size: 210,
      enableSorting: false,
      Cell: ({ row }: { row: { original: T } }) => {
        const svc = row.original;
        const target = targetFor(svc);
        const rowKey = toggleRowKey(target);
        const blocked = toggleBlockedReason(svc);
        const busy = busyKey === rowKey;
        const outcome = outcomes[rowKey];

        return (
          <div className="d-flex flex-column gap-1">
            <div className="d-flex align-items-center gap-2">
              {canToggle ? (
                <>
                  <Form.Check
                    type="switch"
                    id={`toggle-${rowKey}`}
                    checked={svc.effective}
                    disabled={!!blocked || busy}
                    onChange={(e) => requestToggle(target, e.target.checked)}
                    aria-label={`Run ${svc.name} on this deployment`}
                    aria-describedby={blocked ? `toggle-${rowKey}-blocked` : undefined}
                    title={
                      blocked ??
                      (svc.effective
                        ? 'Configured on. Switch to turn off.'
                        : 'Configured off. Switch to turn on.')
                    }
                  />
                  {/* The switch already announces its own checked state, so
                      this is decoration for sighted users only. Making it a
                      <label> instead would override the accessible name with
                      a bare "On", which is useless in a table of seven rows. */}
                  <span
                    aria-hidden="true"
                    className={`fw-semibold ${svc.effective ? 'text-success' : 'text-theme-muted'}`}
                  >
                    {svc.effective ? 'On' : 'Off'}
                  </span>
                </>
              ) : (
                <span
                  className={`fw-semibold ${svc.effective ? 'text-success' : 'text-theme-muted'}`}
                >
                  {svc.effective ? 'On' : 'Off'}
                </span>
              )}
              {busy && <span className="text-theme-muted small">working...</span>}
            </div>
            {blocked && (
              <div id={`toggle-${rowKey}-blocked`} className="text-theme-muted small">
                {blocked}
              </div>
            )}
            {outcome && (
              <div className={`small ${VARIANT_TEXT_CLASS[outcomeVariant(outcome)]}`}>
                <span className="fw-semibold">{outcome.message}</span>
                {outcome.detail && <div className="text-theme-muted">{outcome.detail}</div>}
              </div>
            )}
          </div>
        );
      },
    },
    {
      header: 'Live (PM2)',
      accessorKey: 'state',
      size: 180,
      enableSorting: false,
      Cell: ({ row }: { row: { original: T } }) => <LiveState row={row.original} />,
    },
    {
      header: 'Service',
      accessorKey: 'name',
      size: 220,
      Cell: ({ cell }: { cell: { getValue: () => string } }) => (
        <div className="text-theme small">{cell.getValue()}</div>
      ),
    },
    {
      header: 'Service flag',
      accessorKey: 'envFlag',
      size: 180,
      Cell: ({ cell }: { cell: { getValue: () => string | null } }) => {
        const value = cell.getValue();
        return value ? (
          <div className="text-theme fw-semibold small font-monospace">{value}</div>
        ) : (
          <div className="text-theme-muted small">none</div>
        );
      },
    },
  ];

  if (showSource) {
    columns.push({
      header: 'Source',
      accessorKey: 'workplaceOverride',
      size: 150,
      enableSorting: false,
      Cell: ({ row }: { row: { original: T } }) => {
        const overridden =
          (row.original as WorkplaceService).workplaceOverride !== null &&
          (row.original as WorkplaceService).workplaceOverride !== undefined;
        return overridden ? (
          <BaseTooltip id="deployments-source-overridden" text={SOURCE_OVERRIDDEN_TOOLTIP}>
            <Badge bg="warning" text="dark" role="button" tabIndex={0}>
              overridden here
            </Badge>
          </BaseTooltip>
        ) : (
          <BaseTooltip id="deployments-source-global" text={SOURCE_GLOBAL_TOOLTIP}>
            <Badge bg="secondary" role="button" tabIndex={0}>
              inherits global
            </Badge>
          </BaseTooltip>
        );
      },
    });
  }

  columns.push({
    header: 'PM2 name',
    accessorKey: 'pm2Name',
    Cell: ({ cell }: { cell: { getValue: () => string } }) => (
      <div className="text-theme-muted small font-monospace">{cell.getValue()}</div>
    ),
  });

  return columns;
}

const outcomeVariant = (outcome: ToggleOutcome): string => {
  switch (outcome.kind) {
    case 'ok':
      return 'success';
    case 'pending':
      return 'primary';
    case 'warn':
      return 'warning';
    default:
      return 'danger';
  }
};

/**
 * The live PM2 column.
 *
 * "Enabled, but not running" is called out because it is the case an operator
 * most needs to see: the flag is on, the supervisor tried, and the process is
 * not up. A service missing required environment crash-loops rather than
 * starting, and a green switch on its own would hide that entirely.
 */
function LiveState({ row }: { row: WorkplaceService | GlobalService }) {
  return (
    <StatusDot
      view={liveStateView(row)}
      note={isEnabledButNotRunning(row) ? 'Enabled, but not running' : undefined}
    />
  );
}

interface WorkplaceBodyProps {
  workplace: Workplace;
  canToggle: boolean;
  busyKey: string | null;
  outcomes: Record<string, ToggleOutcome>;
  requestToggle: (target: ToggleTarget, enabled: boolean) => void;
}

function WorkplaceBody({
  workplace,
  canToggle,
  busyKey,
  outcomes,
  requestToggle,
}: WorkplaceBodyProps) {
  const columns = useMemo(
    () =>
      buildServiceColumns<WorkplaceService>({
        canToggle,
        busyKey,
        outcomes,
        requestToggle,
        targetFor: (row) => ({
          scope: 'workplace',
          wpIndex: workplace.index,
          wpLabel: workplace.name || workplace.prefix || `Deployment ${workplace.index}`,
          name: row.name,
          envFlag: row.envFlag,
        }),
        showSource: true,
      }),
    [
      canToggle,
      busyKey,
      outcomes,
      requestToggle,
      workplace.index,
      workplace.name,
      workplace.prefix,
    ],
  );

  const modeCaveat = deploymentModeCaveat(workplace.mode);

  return (
    <div className="d-flex flex-column gap-2">
      <div className="text-theme-muted small">
        Root: <span className="text-theme font-monospace">{workplace.rootDir}</span>
      </div>
      {/* Only a managed deployment actually acts on these switches. In the
          other three modes the flag is still written and still durable, but
          the supervisor will not start the process, so say so once here
          rather than letting every row imply otherwise. */}
      {modeCaveat && (
        <Alert variant="warning" className="mb-0 py-2 small">
          {modeCaveat}
        </Alert>
      )}
      <div className="text-theme-muted small">
        <BaseTooltip
          id={`deployments-configured-help-${workplace.index}`}
          text={CONFIGURED_TOOLTIP}
        >
          <span role="button" tabIndex={0} className="text-decoration-underline">
            Configured
          </span>
        </BaseTooltip>{' '}
        is what this deployment is set up to run.{' '}
        <BaseTooltip id={`deployments-live-help-${workplace.index}`} text={LIVE_TOOLTIP}>
          <span role="button" tabIndex={0} className="text-decoration-underline">
            Live
          </span>
        </BaseTooltip>{' '}
        is what PM2 actually has. Turning a service on takes effect within about ten seconds. No
        supervisor restart is needed.
      </div>
      <BaseTable
        columns={columns}
        rows={workplace.services}
        expandable={false}
        customStyles={{
          cell: { borderBottom: '1px solid var(--color-divider)' },
        }}
        muiTableContainerProps={{
          sx: { overflowY: 'visible', overflowX: 'auto' },
        }}
        customEmptyMessage="No services configured for this deployment"
      />
    </div>
  );
}
